import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIOrganizationUser,
  APIReferenceAttributes,
  APITagAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  FieldGlobalAttributes,
  FilterInterfaceFilter,
} from '@ardoq/api-types';
import { ContextSort, getComponentComparator } from '@ardoq/common-helpers';
import { ARDOQ_DEFAULT_LOCALE, Locale } from '@ardoq/locale';
import { DEFAULT_REFERENCE_LABEL_FILTER } from '@ardoq/filter-interface';
import { DEFAULT_ARDOQ_I18NLOCALE, I18nLocale } from '@ardoq/i18n';
import { ComponentTypesByWorkspaceId } from 'streams/models/componentTypesByWorkspaceId$';
import { ReferenceTypesByWorkspaceId } from 'streams/models/referenceTypesByWorkspaceId$';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { components$ } from 'streams/components/components$';
import { sort$ } from 'streams/context/sort$';
import { locale$ } from 'streams/locale$';

export type GraphModelData = {
  componentTypesByWorkspaceId: ComponentTypesByWorkspaceId;
  referenceTypesByWorkspaceId: ReferenceTypesByWorkspaceId;
  fieldsByComponentType: ReadonlyMap<string, readonly APIFieldAttributes[]>;
  fieldsByReferenceType: ReadonlyMap<string, readonly APIFieldAttributes[]>;
  workspacesById: Readonly<Partial<Record<ArdoqId, APIWorkspaceAttributes>>>;
  orgUsersById: Readonly<Record<ArdoqId, APIOrganizationUser>>;
  allComponents: readonly APIComponentAttributes[];
  allReferences: readonly APIReferenceAttributes[];
  allTags: readonly APITagAttributes[];
  allFields: readonly APIFieldAttributes[];
  referenceLabelFilter: FilterInterfaceFilter;
  labelField: FieldGlobalAttributes | null;
  locale: Locale;
  i18nLocale: I18nLocale;
  includedComponentIds: ReadonlySet<ArdoqId>;
  includedReferenceIds: ReadonlySet<ArdoqId>;
};

export const sortGraphComponents = (
  components: readonly APIComponentAttributes[],
  sort: ContextSort,
  locale: Locale
): APIComponentAttributes[] =>
  [...components].sort(getComponentComparator(sort, locale));

export const sortedGraphComponents$: Observable<APIComponentAttributes[]> =
  combineLatest({
    components: components$,
    sort: sort$,
    locale: locale$,
  }).pipe(
    map(({ components, sort, locale }) =>
      sortGraphComponents(components.models, sort, locale)
    ),
    shareReplay({ refCount: false, bufferSize: 1 })
  );

export const EMPTY_GRAPH_MODEL_DATA: GraphModelData = {
  componentTypesByWorkspaceId: {},
  referenceTypesByWorkspaceId: {},
  fieldsByComponentType: new Map(),
  fieldsByReferenceType: new Map(),
  workspacesById: {},
  orgUsersById: {},
  allComponents: [],
  allReferences: [],
  allTags: [],
  allFields: [],
  referenceLabelFilter: DEFAULT_REFERENCE_LABEL_FILTER,
  labelField: null,
  locale: ARDOQ_DEFAULT_LOCALE,
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  includedComponentIds: new Set(),
  includedReferenceIds: new Set(),
};
